module Widgets.Form.Date where

import Common

import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Argonaut (Json, stringify)
import Data.Argonaut as J
import Data.Array as Arr
import Data.Date (Date, exactDate)
import Data.Date as Date
import Data.DateTime (DateTime(..))
import Data.DateTime as DT
import Data.Enum (fromEnum, toEnum)
import Data.Int as Int
import Data.Maybe (maybe)
import Data.String (Pattern(..))
import Data.String as Str
import Data.Time (Time(..))
import Data.Time as Time
import Data.Traversable (traverse)
import Effect.Class (liftEffect)
import Effect.Console (log)
import GraphQL.Shape (QueryField(..))
import Internal.Debug (debugLog)
import Internal.Luxon (LDateTime)
import Internal.Luxon as Luxon
import Widgets.Form.Class (class FormWidget, FormContext, fieldAccessProps, formWidgetState, fieldClass, fieldId)
import Widgets.Form.Common (widgetLabelS)


newtype DateInput = DateInput DateInputType
type DateInputType = GenDateWidget Date

derive newtype instance showDateInput :: Show DateInput
instance dateFW :: FormWidget DateInput where
    getId (DateInput state) = state.id
    isLeaf _ = true
    asField (DateInput state) = Just $ QScalar state.id
    fieldPaths fc (DateInput state) = [ fieldId fc state.id ]

    toWidget fc (DateInput state) = formWidgetState <$>
        DateInput <$> dateWidget fc "date" state

    getData _ (DateInput state) =
        maybe J.jsonNull (J.fromString <<< showDate) state.value
    setData _ (DateInput state) json = DateInput <$> genSetData state json


newtype DateTimeInput = DateTimeInput DateTimeInputType
type DateTimeInputType = GenDateWidget LDateTime

derive newtype instance showDateTimeInput :: Show DateTimeInput
instance dateTimeFW :: FormWidget DateTimeInput where
    getId (DateTimeInput state) = state.id
    isLeaf _ = true
    asField (DateTimeInput state) = Just $ QScalar state.id
    fieldPaths fc (DateTimeInput state) = [ fieldId fc state.id ]

    toWidget fc (DateTimeInput state) = formWidgetState <$>
        DateTimeInput <$> dateWidget fc "datetime-local" state

    getData _ (DateTimeInput state) =
        maybe J.jsonNull (J.fromString <<< Luxon.toISONoTZ) state.value
    setData _ (DateTimeInput state) json = DateTimeInput <$> genSetData state json


class GenDate d where
    showGenDate :: d -> String
    parseGenDate :: String -> Maybe d

type GenDateWidget val =
    { id :: String
    , label :: String
    , cls :: String
    , value :: Maybe val
    }

dateWidget :: forall val. GenDate val =>
    FormContext -> String -> GenDateWidget val -> Widget HTML (GenDateWidget val)
dateWidget fc htmlType state = handleInput =<< D.div
    [ P.key id, P.className $ fieldClass state ]
    [ widgetLabelS fc state [ P.className "block w-full" ]
    , D.input
        ( [ P._type htmlType, P._id id
        , P.className "widget-input"
        , P.value $ maybe "" showGenDate state.value
        , P.unsafeTargetValue <$> P.onInput
        ] <> fieldAccessProps fc state.id )
    ]
    where
        id = fieldId fc state.id
        handleInput :: String -> Widget HTML (GenDateWidget val)
        handleInput newval = do
            case parseGenDate newval of
                Just result -> pure $ state { value = Just result }
                Nothing -> do
                    liftEffect $ log ("Error parsing date " <> newval)
                    pure $ state { value = Nothing }

genSetData :: forall date. GenDate date =>
    GenDateWidget date -> Json -> Either ErrorWrapper (GenDateWidget date)
genSetData state json | J.isNull json = Right $ state { value = Nothing }
genSetData state json =
    case parseGenDate =<< J.toString json of
         Just date -> Right $ state { value = Just date }
         Nothing ->
             let _ = debugLog $ "Failed to parse datetime: " <> stringify json in
             Right $ state { value = Nothing }


instance genD :: GenDate Date where
    showGenDate = showDate
    parseGenDate = parseInconsistentDate
instance genDT :: GenDate DateTime where
    showGenDate = showDateTime
    parseGenDate = parseDateTime
instance genLDT :: GenDate LDateTime where
    showGenDate = Luxon.toISONoTZ
    parseGenDate = Luxon.fromISO

showDateTime :: DateTime -> String
showDateTime dt =
    (showDate $ DT.date dt) <> "T" <>
    (showTime $ DT.time dt)

showDate :: Date -> String
showDate date =
    (zeroPad 4 $ show $ fromEnum $ Date.year date) <> "-" <>
    (zeroPad 2 $ show $ fromEnum $ Date.month date) <> "-" <>
    (zeroPad 2 $ show $ fromEnum $ Date.day date)

showTime :: Time -> String
showTime time =
    (zeroPad 2 $ show $ fromEnum $ Time.hour time) <> ":" <>
    (zeroPad 2 $ show $ fromEnum $ Time.minute time)

zeroPad :: Int -> String -> String
zeroPad n str = pad <> str
  where
    pad | padlen > 0 = Str.joinWith "" $ Arr.replicate padlen "0"
    pad = ""
    padlen = n - Str.length str

-- | This parses a date according to the "I don't know if it has a time
-- | component because consistency is too boring" format standard by throwing
-- | away the time component (anything after a T) if present.
parseInconsistentDate :: String -> Maybe Date
parseInconsistentDate maybeDateTimeOrJustDateIdk =
    case Str.split (Pattern "T") maybeDateTimeOrJustDateIdk of
         [dstr, _] -> parseDate dstr
         _ -> parseDate maybeDateTimeOrJustDateIdk

parseDateTime :: String -> Maybe DateTime
parseDateTime str = do
    case Str.split (Pattern "T") str of
        [dstr, tstr] -> do
           date <- parseDate dstr
           time <- parseTime tstr
           pure $ DateTime date time
        _ -> Nothing

parseTime :: String -> Maybe Time
parseTime str = do
    parts <- traverse Int.fromString $ Str.split (Pattern ":") str
    case parts of
        [h, m] -> do
           hour <- toEnum h
           minute <- toEnum m
           second <- toEnum 0
           ms <- toEnum 0
           pure $ Time hour minute second ms
        _ -> Nothing

parseDate :: String -> Maybe Date
parseDate str = do
    parts <- traverse Int.fromString $ Str.split (Pattern "-") str
    case parts of
        [y, m, d] -> do
            year <- toEnum y
            month <- toEnum m
            day <- toEnum d
            exactDate year month day
        _ -> Nothing
